import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Wrench,
  Plus,
  PencilSimple,
  Trash,
  Check,
  Warning,
  Printer,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, API, formatApiErrorDetail, photoHref } from "@/lib/api";
import { useDirectory } from "@/lib/directory";
import { FixtureLabel } from "@/components/FixtureLabel";

const FIELD_ROWS = [
  ["sink_brand", "Brand"],
  ["sink_model", "Model"],
  ["sink", "Sink"],
  ["piece", "Piece"],
  ["mount", "Mount"],
  ["faucet_holes", "Faucet Holes"],
  ["accessory_holes", "Accessory Holes"],
];

const EMPTY = {
  area: "",
  piece: "",
  sink: "",
  sink_brand: "",
  sink_model: "",
  sink_id: "",
  mount: "",
  faucet_holes: "",
  accessory_holes: "",
  tags: "",
  warning: "",
  in_stock: true,
  notes: "",
};

const sinkLabel = (s) =>
  [s.brand, s.model].filter(Boolean).join(" ").trim() || s.sink_type || "Sink";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
};

export const FixturesPanel = ({ kind, id, fixtures = [], onUpdated, context = {}, quotedSinks = [] }) => {
  const { nameOf } = useDirectory();
  const [editing, setEditing] = useState(null); // fixture id | "new" | null
  const [draft, setDraft] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sinks, setSinks] = useState([]);
  const [printing, setPrinting] = useState(null); // fixtures being printed as 4x6 labels
  const [logoUrl, setLogoUrl] = useState("");

  useEffect(() => {
    api
      .get("/public/branding")
      .then(({ data }) => {
        if (data?.logo) setLogoUrl(`${API}/public/logo`);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/sinks")
      .then(({ data }) => setSinks(Array.isArray(data) ? data : []))
      .catch(() => setSinks([]));
  }, []);

  const base = `/${kind}/${id}/fixtures`;
  const sinkById = (sid) => sinks.find((s) => s.id === sid);
  // live stock for a fixture: linked sink qty when set, else the manual flag
  const stockFor = (f) => {
    const linked = f.sink_id ? sinkById(f.sink_id) : null;
    if (linked)
      return { linked, qty: linked.quantity ?? 0, min: linked.min_level ?? 0 };
    return { linked: null, qty: null, min: 0 };
  };
  const isOut = (f) => {
    const { linked, qty } = stockFor(f);
    return linked ? qty <= 0 : f.in_stock === false;
  };
  const confirmedCount = fixtures.filter((f) => f.confirmed).length;
  const outOfStock = fixtures.filter(isOut).length;

  const toDraft = (f) => ({
    area: f.area || "",
    piece: f.piece || "",
    sink: f.sink || "",
    sink_brand: f.sink_brand || "",
    sink_model: f.sink_model || "",
    sink_id: f.sink_id || "",
    mount: f.mount || "",
    faucet_holes: f.faucet_holes || "",
    accessory_holes: f.accessory_holes || "",
    tags: (f.tags || []).join(", "),
    warning: f.warning || "",
    in_stock: f.in_stock !== false,
    notes: f.notes || "",
  });

  const payload = () => ({
    area: draft.area,
    piece: draft.piece,
    sink: draft.sink,
    sink_brand: draft.sink_brand,
    sink_model: draft.sink_model,
    sink_id: draft.sink_id,
    mount: draft.mount,
    faucet_holes: draft.faucet_holes,
    accessory_holes: draft.accessory_holes,
    tags: draft.tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean),
    warning: draft.warning,
    in_stock: draft.in_stock,
    notes: draft.notes,
  });

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const cancel = () => {
    setEditing(null);
    setDraft(EMPTY);
  };

  const pickSink = (v) => {
    if (v === "__custom__") {
      setDraft((d) => ({ ...d, sink_id: "" }));
      return;
    }
    const s = sinkById(v);
    setDraft((d) => ({
      ...d,
      sink_id: v,
      sink: s ? sinkLabel(s) : d.sink,
      sink_brand: s?.brand || d.sink_brand,
      sink_model: s?.model || d.sink_model,
    }));
  };

  const applyQuoted = (qs) =>
    setDraft((d) => ({
      ...d,
      sink_id: qs.sink_id || "",
      sink: qs.name || d.sink,
      sink_brand: qs.brand || d.sink_brand,
      sink_model: qs.model || d.sink_model,
    }));

  // Does a fixture's chosen sink match one that's actually on the quote?
  const quotedSinkMatch = (f) => {
    if (!quotedSinks.length) return true;
    const label = [f.sink_brand, f.sink_model].filter(Boolean).join(" ").trim();
    const has = f.sink_id || (f.sink || "").trim() || label;
    if (!has) return true;
    return quotedSinks.some(
      (qs) =>
        (qs.sink_id && f.sink_id && qs.sink_id === f.sink_id) ||
        (qs.name &&
          ((f.sink || "").trim().toLowerCase() === qs.name.toLowerCase() ||
            label.toLowerCase() === qs.name.toLowerCase())),
    );
  };

  const save = async () => {
    if (!draft.area.trim()) {
      toast.error("Add a room / area name");
      return;
    }
    setBusy(true);
    try {
      const { data } =
        editing === "new"
          ? await api.post(base, payload())
          : await api.put(`${base}/${editing}`, payload());
      onUpdated(data);
      cancel();
      toast.success("Fixture saved");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
      );
    }
    setBusy(false);
  };

  const toggle = async (f) => {
    try {
      const { data } = await api.post(`${base}/${f.id}/confirm`);
      onUpdated(data);
    } catch {
      toast.error("Could not update");
    }
  };

  const remove = async (f) => {
    try {
      const { data } = await api.delete(`${base}/${f.id}`);
      onUpdated(data);
      toast.success("Fixture removed");
    } catch {
      toast.error("Could not remove");
    }
  };

  const uploadPhoto = async (f, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`${base}/${f.id}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpdated(data);
      toast.success("Reference photo added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not upload photo");
    }
  };

  const removePhoto = async (f) => {
    try {
      const { data } = await api.delete(`${base}/${f.id}/photo`);
      onUpdated(data);
      toast.success("Photo removed");
    } catch {
      toast.error("Could not remove photo");
    }
  };

  const printLabels = (list) => {
    setPrinting(list);
    setTimeout(() => {
      document.body.classList.add("printing-piece");
      window.print();
      document.body.classList.remove("printing-piece");
    }, 300);
  };

  const fieldCls = "mt-1 h-9 border-zinc-800 bg-zinc-950 text-sm";
  const labelCls =
    "text-[10px] font-bold uppercase tracking-widest text-zinc-500";

  const form = (
    <div
      className="space-y-3 rounded-lg border border-indigo-500/30 bg-zinc-900 p-4"
      data-testid="fixture-form"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        {[
          ["area", "Room / area", "Kitchen"],
          ["piece", "Piece", "vanity"],
          ["mount", "Mount", "under"],
          ["faucet_holes", "Faucet holes", '3 (8" spread)'],
          ["accessory_holes", "Accessory holes", 'air switch right 2"'],
        ].map(([k, label, ph]) => (
          <label key={k} className="block">
            <span className={labelCls}>{label}</span>
            <Input
              data-testid={`fixture-field-${k}`}
              className={fieldCls}
              value={draft[k]}
              placeholder={ph}
              onChange={(e) => set(k, e.target.value)}
            />
          </label>
        ))}
      </div>

      {quotedSinks.length > 0 && (
        <div
          className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3"
          data-testid="fixture-quoted-sinks"
        >
          <span className={labelCls}>
            On the quote
            {quotedSinks[0]?.quote_code ? ` · ${quotedSinks[0].quote_code}` : ""}
          </span>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {quotedSinks.map((qs, i) => (
              <button
                key={i}
                type="button"
                data-testid={`fixture-use-quoted-${i}`}
                onClick={() => applyQuoted(qs)}
                className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-xs font-semibold text-amber-200 transition-colors duration-150 hover:bg-amber-500/20"
              >
                Use {qs.name}
                {qs.qty > 1 ? ` ×${qs.qty}` : ""}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="block">
        <span className={labelCls}>Sink</span>
        <div className="mt-1 grid gap-2 sm:grid-cols-2">
          <Select
            value={draft.sink_id || "__custom__"}
            onValueChange={pickSink}
          >
            <SelectTrigger
              data-testid="fixture-field-sink-stock"
              className="h-9 border-zinc-800 bg-zinc-950 text-sm"
            >
              <SelectValue placeholder="Link a stock sink" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__custom__">
                Custom / customer-supplied
              </SelectItem>
              {sinks.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {sinkLabel(s)} · {s.quantity ?? 0} on hand
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            data-testid="fixture-field-sink"
            className="h-9 border-zinc-800 bg-zinc-950 text-sm"
            value={draft.sink}
            placeholder="Sink description"
            onChange={(e) => set("sink", e.target.value)}
          />
        </div>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <label className="block">
            <span className={labelCls}>Brand</span>
            <Input
              data-testid="fixture-field-sink-brand"
              className={fieldCls}
              value={draft.sink_brand}
              placeholder="Ruvati"
              onChange={(e) => set("sink_brand", e.target.value)}
            />
          </label>
          <label className="block">
            <span className={labelCls}>Model</span>
            <Input
              data-testid="fixture-field-sink-model"
              className={fieldCls}
              value={draft.sink_model}
              placeholder="RVG2027BK"
              onChange={(e) => set("sink_model", e.target.value)}
            />
          </label>
        </div>
        {draft.sink_id ? (
          <p className="mt-1 text-[10px] text-emerald-400/80">
            Linked to inventory — stock is read live from the sinks catalog.
          </p>
        ) : (
          <label className="mt-2 flex items-center gap-2 text-sm text-zinc-200">
            <input
              type="checkbox"
              data-testid="fixture-field-instock"
              checked={draft.in_stock}
              onChange={(e) => set("in_stock", e.target.checked)}
              className="h-4 w-4 accent-emerald-500"
            />
            In stock / on hand
          </label>
        )}
      </div>

      <label className="block">
        <span className={labelCls}>Tags (comma separated)</span>
        <Input
          data-testid="fixture-field-tags"
          className={fieldCls}
          value={draft.tags}
          placeholder="Air Switch, Soap Dispenser"
          onChange={(e) => set("tags", e.target.value)}
        />
      </label>
      <label className="block">
        <span className={labelCls}>Flag / warning banner</span>
        <Input
          data-testid="fixture-field-warning"
          className={fieldCls}
          value={draft.warning}
          placeholder="Bridge faucet — no center hole"
          onChange={(e) => set("warning", e.target.value)}
        />
      </label>
      <div className="flex gap-2">
        <button
          type="button"
          data-testid="fixture-save"
          disabled={busy}
          onClick={save}
          className="flex h-9 items-center gap-1.5 rounded-lg bg-indigo-500 px-4 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400 disabled:opacity-50"
        >
          <Check size={14} weight="bold" /> Save
        </button>
        <button
          type="button"
          data-testid="fixture-cancel"
          onClick={cancel}
          className="flex h-9 items-center rounded-lg border border-zinc-700 px-4 text-xs font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="fixtures-panel">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-400">
            <Wrench size={14} weight="bold" className="text-indigo-300" />
            Materials — Sinks & Cutouts
          </h3>
          <p
            className="mt-0.5 text-xs text-zinc-500"
            data-testid="fixtures-count"
          >
            {confirmedCount} of {fixtures.length} confirmed
            {outOfStock > 0 && (
              <span className="text-red-400"> · {outOfStock} not in stock</span>
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {fixtures.length > 0 && (
            <button
              type="button"
              data-testid="fixture-print-all"
              onClick={() => printLabels(fixtures)}
              className="flex h-9 items-center gap-1.5 rounded-lg border border-zinc-800 px-3.5 text-xs font-bold uppercase tracking-widest text-zinc-200 transition-colors duration-150 hover:border-indigo-400/50 hover:bg-zinc-800"
            >
              <Printer size={14} weight="bold" /> Labels
            </button>
          )}
          {editing !== "new" && (
            <button
              type="button"
              data-testid="fixture-add"
              onClick={() => {
                setEditing("new");
                setDraft(EMPTY);
              }}
              className="flex h-9 items-center gap-1.5 rounded-lg border border-zinc-800 px-3.5 text-xs font-bold uppercase tracking-widest text-zinc-200 transition-colors duration-150 hover:border-indigo-400/50 hover:bg-zinc-800"
            >
              <Plus size={14} weight="bold" /> Add Fixture
            </button>
          )}
        </div>
      </div>

      {editing === "new" && form}

      {fixtures.length === 0 && editing !== "new" && (
        <div
          className="rounded-lg border border-dashed border-zinc-800 bg-zinc-900/50 p-8 text-center"
          data-testid="fixtures-empty"
        >
          <p className="text-sm font-semibold text-zinc-300">
            No fixtures listed yet
          </p>
          <p className="mt-1 text-xs text-zinc-500">
            Add sinks, faucet holes and cutouts so the shop and install crew can
            confirm everything's on hand.
          </p>
        </div>
      )}

      {fixtures.map((f) => {
        if (editing === f.id) return <div key={f.id}>{form}</div>;
        const { linked, qty, min } = stockFor(f);
        return (
          <div
            key={f.id}
            data-testid={`fixture-card-${f.id}`}
            className={`rounded-lg border p-4 ${
              f.warning
                ? "border-red-500/40 bg-red-500/10"
                : f.confirmed
                  ? "border-emerald-500/30 bg-emerald-500/10"
                  : "border-zinc-800 bg-zinc-900"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <h4 className="text-base font-bold tracking-tight text-zinc-100">
                {f.area || "Fixture"}
              </h4>
              <div className="flex shrink-0 items-center gap-1.5">
                {!quotedSinkMatch(f) && (
                  <span
                    data-testid={`fixture-offquote-${f.id}`}
                    className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300"
                    title="This sink isn't the one on the quote"
                  >
                    Not on quote
                  </span>
                )}
                {f.sink_allocation_id && (
                  <span
                    data-testid={`fixture-reserved-${f.id}`}
                    className="rounded-full bg-indigo-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-indigo-300"
                  >
                    Sink reserved
                  </span>
                )}
                {linked ? (
                (() => {
                  const low = qty > 0 && qty <= min;
                  return (
                    <span
                      data-testid={`fixture-stock-${f.id}`}
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                        qty <= 0
                          ? "bg-red-600 text-white"
                          : low
                            ? "bg-amber-500 text-black"
                            : "bg-emerald-500/20 text-emerald-300"
                      }`}
                    >
                      {qty <= 0
                        ? "Out of stock"
                        : low
                          ? `Low · ${qty}`
                          : `In stock · ${qty}`}
                    </span>
                  );
                })()
              ) : (
                f.in_stock === false && (
                  <span
                    data-testid={`fixture-stock-${f.id}`}
                    className="shrink-0 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white"
                  >
                    Not in stock
                  </span>
                )
              )}
              </div>
            </div>

            {f.warning && (
              <div
                data-testid={`fixture-warning-${f.id}`}
                className="mt-2 flex items-center gap-2 rounded-md bg-red-600 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white"
              >
                <Warning size={14} weight="fill" /> {f.warning}
              </div>
            )}

            <dl className="mt-3 space-y-1">
              {FIELD_ROWS.filter(([k]) => f[k]).map(([k, label]) => (
                <div
                  key={k}
                  className="grid grid-cols-[120px_1fr] gap-3 text-sm"
                >
                  <dt className="text-zinc-500">{label}</dt>
                  <dd className="text-zinc-200">{f[k]}</dd>
                </div>
              ))}
            </dl>

            {(f.tags || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {f.tags.map((t, i) => (
                  <span
                    key={i}
                    className="rounded-md bg-indigo-500/15 px-2 py-0.5 text-xs font-semibold text-indigo-200"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}

            <div className="mt-3" data-testid={`fixture-photo-${f.id}`}>
              {f.photo?.storage_path ? (
                <div className="flex items-start gap-3">
                  <a
                    href={photoHref(f.photo.storage_path)}
                    target="_blank"
                    rel="noreferrer"
                    className="block shrink-0"
                  >
                    <img
                      src={photoHref(f.photo.thumb_path || f.photo.storage_path)}
                      alt="Fixture reference"
                      className="h-20 w-20 rounded-lg border border-zinc-700 object-cover"
                    />
                  </a>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      Reference photo
                    </span>
                    <label className="cursor-pointer text-xs font-semibold text-indigo-300 hover:text-indigo-200">
                      Replace
                      <input
                        type="file"
                        accept="image/*"
                        data-testid={`fixture-photo-replace-${f.id}`}
                        className="hidden"
                        onChange={(e) => {
                          uploadPhoto(f, e.target.files?.[0]);
                          e.target.value = "";
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      data-testid={`fixture-photo-remove-${f.id}`}
                      onClick={() => removePhoto(f)}
                      className="text-left text-xs font-semibold text-zinc-500 hover:text-red-400"
                    >
                      Remove photo
                    </button>
                  </div>
                </div>
              ) : (
                <label className="flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-zinc-700 text-[11px] font-bold uppercase tracking-widest text-zinc-400 hover:border-indigo-400/50 hover:text-indigo-200">
                  <Plus size={14} weight="bold" /> Add reference photo
                  <input
                    type="file"
                    accept="image/*"
                    data-testid={`fixture-photo-add-${f.id}`}
                    className="hidden"
                    onChange={(e) => {
                      uploadPhoto(f, e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                </label>
              )}
            </div>

            {f.confirmed && (
              <p
                className="mt-3 text-xs font-semibold text-emerald-400"
                data-testid={`fixture-confirmed-by-${f.id}`}
              >
                Confirmed by {nameOf(f.confirmed_by) || "team"}
                {f.confirmed_at ? ` · ${fmtDate(f.confirmed_at)}` : ""}
              </p>
            )}

            <button
              type="button"
              data-testid={`fixture-confirm-${f.id}`}
              onClick={() => toggle(f)}
              className={`mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-lg text-sm font-bold tracking-tight transition-colors duration-150 ${
                f.confirmed
                  ? "bg-emerald-600 text-white hover:bg-emerald-500"
                  : "border border-zinc-700 text-zinc-200 hover:bg-zinc-800"
              }`}
            >
              {f.confirmed ? (
                <>
                  <Check size={16} weight="bold" /> Confirmed — tap to unconfirm
                </>
              ) : (
                "Tap to confirm ready"
              )}
            </button>

            <div className="mt-2 flex items-center gap-4">
              <button
                type="button"
                data-testid={`fixture-print-${f.id}`}
                onClick={() => printLabels([f])}
                className="flex items-center gap-1 text-xs font-semibold text-zinc-400 hover:text-indigo-200"
              >
                <Printer size={13} weight="bold" /> 4×6 Label
              </button>
              <button
                type="button"
                data-testid={`fixture-edit-${f.id}`}
                onClick={() => {
                  setEditing(f.id);
                  setDraft(toDraft(f));
                }}
                className="flex items-center gap-1 text-xs font-semibold text-indigo-300 hover:text-indigo-200"
              >
                <PencilSimple size={13} weight="bold" /> Edit
              </button>
              <button
                type="button"
                data-testid={`fixture-delete-${f.id}`}
                onClick={() => remove(f)}
                className="flex items-center gap-1 text-xs font-semibold text-zinc-500 hover:text-red-400"
              >
                <Trash size={13} weight="bold" /> Remove
              </button>
            </div>
          </div>
        );
      })}

      {printing && (
        <div
          id="piece-print-area"
          className="mt-4"
          data-testid="fixture-label-print-area"
        >
          <div className="no-print mb-2 flex items-center justify-between gap-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              Label preview — {printing.length} label
              {printing.length === 1 ? "" : "s"} · 4 × 6 in
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="fixture-label-print-again"
                onClick={() => printLabels(printing)}
                className="flex h-9 items-center gap-1.5 rounded-lg bg-indigo-500 px-3.5 text-[10px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400"
              >
                <Printer size={13} weight="bold" /> Print
              </button>
              <button
                type="button"
                data-testid="fixture-label-close"
                onClick={() => setPrinting(null)}
                className="flex h-9 items-center rounded-lg border border-zinc-700 px-3.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
              >
                Close
              </button>
            </div>
          </div>
          <div className="label-stack space-y-4">
            {printing.map((f) => (
              <div
                key={f.id}
                data-testid={`fixture-label-wrap-${f.id}`}
                className="origin-top-left scale-[0.72] sm:scale-100"
                style={{ width: "4in" }}
              >
                <FixtureLabel
                  fixture={{
                    ...f,
                    _sink: f.sink_id ? sinkById(f.sink_id) : null,
                    _reserved: !!f.sink_allocation_id,
                  }}
                  context={{ ...context, logoUrl }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
